import type { LinkStatus, PartnerName } from '@regie/api-contract'
import type { LucideIcon } from 'lucide-react'
import {
  Archive,
  CheckCircle2,
  CircleSlash,
  Clock,
  Coins,
  PauseCircle,
  Radar,
  Radio,
  Sparkles,
  Unplug,
  XCircle,
} from 'lucide-react'

export type StatusTone =
  'neutral' | 'info' | 'processing' | 'ready' | 'pending' | 'success' | 'danger' | 'warning'

export interface StatusMeta {
  tone: StatusTone
  icon: LucideIcon
  labelKey: string
  pulse?: boolean
}

/**
 * Free-string resource status (property / affiliate partner) → tone. The API
 * types `status` as an open string; known values get a tone, everything else
 * falls back to `neutral` (and is still rendered verbatim by the caller).
 */
export function statusTone(status: string): StatusTone {
  switch (status) {
    case 'active':
      return 'success'
    case 'paused':
      return 'pending'
    case 'disabled':
      return 'danger'
    case 'archived':
      return 'neutral'
    default:
      return 'neutral'
  }
}

/** Affiliate-link lifecycle → tone/icon/label. Exhaustive over LinkStatus. */
export const LINK_STATUS_META: Record<LinkStatus, StatusMeta> = {
  active: { tone: 'success', icon: Radio, labelKey: 'status.link.active' },
  archived: { tone: 'neutral', icon: Archive, labelKey: 'status.link.archived' },
}

/**
 * Opportunities-dashboard diagnosis, derived client-side per targetKey. These
 * three read at a glance:
 *   monetized     → verdigris: unpaid traffic is zero, a link is active.
 *   opportunity   → struck brass: no active link + unpaid traffic (sign a program).
 *   leaking       → rust: a link IS active but attribution still leaks.
 *   tracking_leak → rust: the network counted far more clicks than we declared
 *                   (the site under-tracks its outbound clicks). Takes priority
 *                   over the others — the gap invalidates our monetized split.
 */
export type ClickDiagnosis = 'monetized' | 'opportunity' | 'leaking' | 'tracking_leak'

export const CLICK_DIAGNOSIS_META: Record<ClickDiagnosis, StatusMeta> = {
  monetized: { tone: 'success', icon: Coins, labelKey: 'status.click.monetized' },
  opportunity: { tone: 'warning', icon: Sparkles, labelKey: 'status.click.opportunity' },
  leaking: { tone: 'danger', icon: Unplug, labelKey: 'status.click.leaking' },
  tracking_leak: { tone: 'danger', icon: Radar, labelKey: 'status.click.tracking_leak' },
}

/**
 * Conversion lifecycle → tone/icon/label. `state` is a free string server-side,
 * so this is keyed loosely: known network states get a tone, anything else
 * falls back to neutral (and is rendered verbatim by the caller).
 *   validated        → verdigris success: approved, the only uploadable state.
 *   pending_approval → brass pending: the network has not ruled yet.
 *   rejected         → rust danger: declined, no commission.
 */
export function conversionStateTone(state: string): StatusTone {
  switch (state) {
    case 'validated':
      return 'success'
    case 'pending_approval':
      return 'pending'
    case 'rejected':
      return 'danger'
    default:
      return 'neutral'
  }
}

export const CONVERSION_STATE_ICON: Record<string, LucideIcon> = {
  validated: CheckCircle2,
  pending_approval: Clock,
  rejected: XCircle,
}

/** Generic resource-status meta for badges that want an icon (paused/disabled). */
export const RESOURCE_STATUS_ICON: Record<string, LucideIcon> = {
  active: Radio,
  paused: PauseCircle,
  archived: Archive,
  disabled: CircleSlash,
}

/** Prebuilt Tailwind utility strings per tone — components never hand-roll these. */
export const TONE_CLASSES: Record<
  StatusTone,
  { soft: string; solid: string; fg: string; dot: string }
> = {
  neutral: {
    soft: 'bg-tone-neutral-soft text-tone-neutral-fg',
    solid: 'bg-tone-neutral text-background',
    fg: 'text-tone-neutral-fg',
    dot: 'bg-tone-neutral',
  },
  info: {
    soft: 'bg-tone-info-soft text-tone-info-fg',
    solid: 'bg-tone-info text-background',
    fg: 'text-tone-info-fg',
    dot: 'bg-tone-info',
  },
  processing: {
    soft: 'bg-tone-processing-soft text-tone-processing-fg',
    solid: 'bg-tone-processing text-background',
    fg: 'text-tone-processing-fg',
    dot: 'bg-tone-processing',
  },
  ready: {
    soft: 'bg-tone-ready-soft text-tone-ready-fg',
    solid: 'bg-tone-ready text-background',
    fg: 'text-tone-ready-fg',
    dot: 'bg-tone-ready',
  },
  pending: {
    soft: 'bg-tone-pending-soft text-tone-pending-fg',
    solid: 'bg-tone-pending text-background',
    fg: 'text-tone-pending-fg',
    dot: 'bg-tone-pending',
  },
  success: {
    soft: 'bg-tone-success-soft text-tone-success-fg',
    solid: 'bg-tone-success text-background',
    fg: 'text-tone-success-fg',
    dot: 'bg-tone-success',
  },
  danger: {
    soft: 'bg-tone-danger-soft text-tone-danger-fg',
    solid: 'bg-tone-danger text-background',
    fg: 'text-tone-danger-fg',
    dot: 'bg-tone-danger',
  },
  warning: {
    soft: 'bg-tone-warning-soft text-tone-warning-fg',
    solid: 'bg-tone-warning text-background',
    fg: 'text-tone-warning-fg',
    dot: 'bg-tone-warning',
  },
}

/** Affiliate-network identity metadata — colorVar points at --partner-* tokens. */
export const PARTNER_META: Record<PartnerName, { colorVar: string; softVar: string }> = {
  Kwanko: { colorVar: '--partner-kwanko', softVar: '--partner-kwanko-soft' },
  TimeOne: { colorVar: '--partner-timeone', softVar: '--partner-timeone-soft' },
  Awin: { colorVar: '--partner-awin', softVar: '--partner-awin-soft' },
  MobiMatter: { colorVar: '--partner-mobimatter', softVar: '--partner-mobimatter-soft' },
  Impact: { colorVar: '--partner-impact', softVar: '--partner-impact-soft' },
}
